import { t } from 'i18next';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isMobile } from 'react-device-detect';

import { CloseIcon, MaximizeIcon, MinimizeIcon } from '../../icons';
import Btn, { BtnType } from '../Buttons/Btn';
import * as s from './styles';

export type ModalProps = {
  title: string;
  showModal: boolean;
  closeModal: () => void;
  children?: ReactNode;
  action?: () => void;
  actionNameBtn?: string | undefined;
  hideActionButton?: boolean;
  cancelButton?: () => void;
  disableActionBtn?: boolean;
  isLoading?: boolean;
  onlyCloseButton?: boolean;
  hideFooter?: boolean;
  hideHeader?: boolean;
  hideCloseIcon?: boolean;
  style?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
  additionalActions?: {
    name: string;
    onClick: () => void;
    type?: BtnType;
    style?: React.CSSProperties;
    disable?: boolean;
  }[];
  maximized?: boolean;
};

const Modal = ({
  title,
  showModal,
  closeModal,
  children,
  action,
  cancelButton,
  actionNameBtn,
  hideActionButton,
  disableActionBtn,
  isLoading,
  onlyCloseButton,
  hideFooter = false,
  hideHeader = false,
  hideCloseIcon,
  style,
  bodyStyle,
  additionalActions,
  maximized,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isMaximized, setIsMaximized] = useState<boolean>(maximized ?? false);

  useEffect(() => {
    if (maximized === undefined && isMobile) {
      setIsMaximized(true);
    }
  },[]);

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const actionButtons = () => {
    return (
      <>
        {additionalActions?.map((action) => (
          <Btn
            key={action.name}
            text={action.name}
            onClick={action.onClick}
            type={action.type ?? 'contained'}
            style={action.style ?? { maxWidth: '150px' }}
            disabled={action.disable ?? (disableActionBtn || isLoading)}
          />
        ))}

        {!hideActionButton && action && (
          <Btn
            text={actionNameBtn ?? t('save')}
            type="contained"
            onClick={action}
            style={{ maxWidth: '100px' }}
            disabled={disableActionBtn || isLoading}
          />
        )}
      </>
    );
  };

  if (!showModal) {
    return null;
  }

  return createPortal(
    <s.ModalContainer>
      <s.ModalContent
        id={isLoading ? 'ldg1' : ''}
        isLoading={isLoading}
        style={style}
        ref={modalRef}
        maximized={isMaximized}
      >
        {!hideHeader && <s.ModalHeader>
          <div id="modal-title">{title}</div>
          <div id="modal-header-icons">
            {isMaximized ? (
              <MinimizeIcon title={t('minimize')} onClick={toggleMaximize} />
            ) : (
              <MaximizeIcon title={t('maximize')} onClick={toggleMaximize} />
            )}

            {!hideCloseIcon && (
              <CloseIcon onClick={isLoading ? () => null : closeModal} title={t('close')} />
            )}
          </div>
        </s.ModalHeader>}

        <s.ModalBody $hideFooter={hideFooter} $hideHeader={hideHeader} style={bodyStyle}>{children}</s.ModalBody>

        {!hideFooter && (onlyCloseButton ? (
          <s.ModalFooter>
            <div className="buttons-footer">
              <Btn text={t('close')} onClick={closeModal} disabled={isLoading} />
            </div>
          </s.ModalFooter>
        ) : (
          <s.ModalFooter>
            {cancelButton ? (
              <div className="buttons-footer">
                <Btn
                  text={t('cancel')}
                  type="text"
                  onClick={cancelButton}
                  style={{ maxWidth: '100px' }}
                />
                {actionButtons()}
              </div>
            ) : (
              <div className="buttons-footer">{actionButtons()}</div>
            )}
          </s.ModalFooter>
        ))}
      </s.ModalContent>
    </s.ModalContainer>,
    document.body,
  );
};

Modal.defaultProps = {
  children: null,
  action: () => null,
  actionNameBtn: undefined,
  hideActionButton: false,
  cancelButton: undefined,
  disableActionBtn: false,
  isLoading: false,
  onlyCloseButton: false,
  hideFooter: false,
  hideHeader: false,
  hideCloseIcon: false,
  style: {},
  bodyStyle: {},
};

export default Modal;
